/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module table/utils/ui/contextualballoon
 */
import { centeredBalloonPositionForLongWidgets } from 'ckeditor5/src/widget';
import { Rect } from 'ckeditor5/src/utils';
import { BalloonPanelView } from 'ckeditor5/src/ui';
import { getTableWidgetAncestor } from './widget';
var DEFAULT_BALLOON_POSITIONS = BalloonPanelView.defaultPositions;
var BALLOON_POSITIONS = [DEFAULT_BALLOON_POSITIONS.northArrowSouth, DEFAULT_BALLOON_POSITIONS.northArrowSouthWest, DEFAULT_BALLOON_POSITIONS.northArrowSouthEast, DEFAULT_BALLOON_POSITIONS.southArrowNorth, DEFAULT_BALLOON_POSITIONS.southArrowNorthWest, DEFAULT_BALLOON_POSITIONS.southArrowNorthEast];
var TABLE_PROPERTIES_BALLOON_POSITIONS = [].concat(BALLOON_POSITIONS, [centeredBalloonPositionForLongWidgets]);
/**
 * A helper utility that positions the
 * {@link module:ui/panel/balloon/contextualballoon~ContextualBalloon contextual balloon} instance
 * with respect to the table in the editor content, if one is selected.
 *
 * @param {module:core/editor/editor~Editor} editor The editor instance.
 * @param {String} target Either "cell" or "table". Determines the target the balloon will
 * be attached to.
 */

export function repositionContextualBalloon(editor, target) {
  var balloon = editor.plugins.get('ContextualBalloon');

  if (getTableWidgetAncestor(editor.editing.view.document.selection)) {
    var position;

    if (target === 'cell') {
      position = getBalloonCellPositionData(editor);
    } else {
      position = getBalloonTablePositionData(editor);
    }

    balloon.updatePosition(position);
  }
}
/**
 * Returns the positioning options that control the geometry of the
 * {@link module:ui/panel/balloon/contextualballoon~ContextualBalloon contextual balloon} with respect
 * to the selected table in the editor content.
 *
 * @param {module:core/editor/editor~Editor} editor The editor instance.
 * @returns {module:utils/dom/position~Options}
 */

export function getBalloonTablePositionData(editor) {
  var firstPosition = editor.model.document.selection.getFirstPosition();
  var modelTable = firstPosition.findAncestor('table');
  var viewTable = editor.editing.mapper.toViewElement(modelTable);
  return {
    target: editor.editing.view.domConverter.viewToDom(viewTable),
    positions: TABLE_PROPERTIES_BALLOON_POSITIONS
  };
}
/**
 * Returns the positioning options that control the geometry of the
 * {@link module:ui/panel/balloon/contextualballoon~ContextualBalloon contextual balloon} with respect
 * to the selected table cell in the editor content.
 *
 * @param {module:core/editor/editor~Editor} editor The editor instance.
 * @returns {module:utils/dom/position~Options}
 */

export function getBalloonCellPositionData(editor) {
  var mapper = editor.editing.mapper;
  var domConverter = editor.editing.view.domConverter;
  var selection = editor.model.document.selection;

  if (selection.rangeCount > 1) {
    return {
      target: function target() {
        return createBoundingRect(selection.getRanges(), editor);
      },
      positions: BALLOON_POSITIONS
    };
  }

  var modelTableCell = getTableCellAtPosition(selection.getFirstPosition());
  var viewTableCell = mapper.toViewElement(modelTableCell);
  return {
    target: domConverter.viewToDom(viewTableCell),
    positions: BALLOON_POSITIONS
  };
} // Returns the first selected table cell from a multi-cell or in-cell selection.
//
// @param {module:engine/model/position~Position} position Document position.
// @returns {module:engine/model/element~Element}

function getTableCellAtPosition(position) {
  var isTableCellSelected = position.nodeAfter && position.nodeAfter.is('element', 'tableCell');
  return isTableCellSelected ? position.nodeAfter : position.findAncestor('tableCell');
} // Returns bounding rectangle for given model ranges.
//
// @param {Iterable.<module:engine/model/range~Range>} ranges Model ranges that the bounding rect should be returned for.
// @param {module:core/editor/editor~Editor} editor The editor instance.
// @returns {module:utils/dom/rect~Rect}


function createBoundingRect(ranges, editor) {
  var mapper = editor.editing.mapper;
  var domConverter = editor.editing.view.domConverter;
  var rects = Array.from(ranges).map(function (range) {
    var modelTableCell = getTableCellAtPosition(range.start);
    var viewTableCell = mapper.toViewElement(modelTableCell);
    return new Rect(domConverter.viewToDom(viewTableCell));
  });
  return Rect.getBoundingRect(rects);
}